import type { ApiErrorResponse } from '@/shared/api'
import { i18n } from '@/app/providers/i18n'

// Create a custom event for API errors
export const API_ERROR_EVENT = 'api-error'
export const WB_TOKEN_ERROR_EVENT = 'wb-token-error'
export const WB_API_ERROR_EVENT = 'wb-api-error'
export const INVALID_API_KEY_ERROR_EVENT = 'invalid-api-key-error'
export const STRUCTURED_API_ERROR_EVENT = 'structured-api-error'

// Interface for the error event detail
export interface ApiErrorEventDetail {
  title: string
  message: string
  errorData: ApiErrorResponse
}

// Interface for wb token error event detail
export interface WbTokenErrorEventDetail {
  title: string
  message: string
  statusCode: number
  error: string
}

// Interface for wb API error event detail (general WB errors)
export interface WbApiErrorEventDetail {
  title: string
  message: string
  statusCode: number
}

// Interface for wb_error response structure
export interface WbErrorResponse {
  error: string
  detail: {
    api: string
    message: string
    status_code: number
    response: string
  }
}

// Interface for invalid API key error response
export interface InvalidApiKeyErrorResponse {
  error: string
  message: string
}

// Interface for invalid API key error event detail
export interface InvalidApiKeyErrorEventDetail {
  title: string
  message: string
}

// Interface for structured API error response (with errors array)
export interface StructuredApiErrorResponse {
  status_code: number
  errors: Array<{
    error: string
    message: string
  }>
}

// Interface for structured API error event detail
export interface StructuredApiErrorEventDetail {
  title: string
  message: string
  statusCode: number
  errors: Array<{
    error: string
    message: string
  }>
}

/**
 * Dispatch an API error event that can be listened to by Vue components
 * @param errorData The error response data
 */
export function dispatchApiErrorEvent(errorData: ApiErrorResponse): void {
  if (!errorData.visibility) 
    return

  // Create and dispatch a custom event with the error details
  const errorEvent = new CustomEvent<ApiErrorEventDetail>(API_ERROR_EVENT, {
    detail: {
      title: errorData.title || 'Error',
      message: errorData.message || 'An unexpected error occurred',
      errorData,
    },
    bubbles: true,
    cancelable: true,
  })

  // Dispatch the event on the window object so it can be caught anywhere
  window.dispatchEvent(errorEvent)
}

/**
 * Dispatch a wb token error event to show API key update modal
 * @param errorDetail The wb error details
 */
export function dispatchWbTokenErrorEvent(
  errorDetail: WbTokenErrorEventDetail,
): void {
  const errorEvent = new CustomEvent<WbTokenErrorEventDetail>(
    WB_TOKEN_ERROR_EVENT,
    {
      detail: errorDetail,
      bubbles: true,
      cancelable: true,
    },
  )

  window.dispatchEvent(errorEvent)
}

// Map of common WB API error messages to translated versions
// NOTE: Only add specific, user-friendly translations here.
// Generic errors like "Invalid Params: xyz" should NOT be translated
// to preserve the specific error details for debugging.
const WB_ERROR_TRANSLATIONS: Record<string, string> = {
  'insufficient funds in the account': 'Недостаточно средств на счете',
  'Invalid Params or payload: insufficient funds in the account':
    'Недостаточно средств на счете для установки этого бюджета',
  'campaign not found': 'Кампания не найдена',
  'campaign already started': 'Кампания уже запущена',
  'campaign already paused': 'Кампания уже приостановлена',
  'campaign already stopped': 'Кампания уже остановлена',
  'budget must be greater than':
    'Бюджет должен быть больше минимального значения',
  'invalid time format': 'Неверный формат времени',
  'too many requests':
    'Слишком много запросов к Wildberries. Попробуйте позже.',
  'Limited by global limiter':
    'Слишком много запросов к Wildberries. Попробуйте позже.',
}

/**
 * Translate WB API error message to Russian if translation exists
 * @param message The original error message
 * @returns Translated message or original if no translation found
 */
function translateWbError(message: string): string {
  // Check for exact match first
  if (WB_ERROR_TRANSLATIONS[message]) {
    return WB_ERROR_TRANSLATIONS[message]
  }

  // Check if message contains any known error patterns
  for (const [pattern, translation] of Object.entries(WB_ERROR_TRANSLATIONS)) {
    if (message.toLowerCase().includes(pattern.toLowerCase())) {
      return translation
    }
  }

  return message
}

/**
 * Dispatch a wb API error event to show error toast for general WB errors
 * @param wbError The wb error response
 */
export function dispatchWbApiErrorEvent(wbError: WbErrorResponse): void {
  const statusCode = wbError.detail?.status_code
  let message = wbError.detail?.message || 'Wildberries API error'

  // Status-code specific overrides take precedence — for known codes the
  // payload-derived message is irrelevant and parsing it just wastes work.
  if (statusCode === 429) {
    message = 'Слишком много запросов к Wildberries. Попробуйте позже.'
  }
  else {
    try {
      const match = message.match(/\{[\s\S]*\}/)
      if (match) {
        const innerJson = JSON.parse(match[0])
        if (typeof innerJson.detail === 'string' && innerJson.detail.trim()) {
          message = innerJson.detail
        }
        else if (typeof innerJson.title === 'string' && innerJson.title.trim()) {
          message = innerJson.title
        }
        else if (typeof innerJson.error === 'string' && innerJson.error.trim()) {
          message = innerJson.error
        }
      }
    }
    catch {
      // Keep the original message if parsing fails
    }

    message = translateWbError(message)
  }

  const errorEvent = new CustomEvent<WbApiErrorEventDetail>(
    WB_API_ERROR_EVENT,
    {
      detail: {
        title: 'Wildberries',
        message,
        statusCode: wbError.detail?.status_code || 400,
      },
      bubbles: true,
      cancelable: true,
    },
  )

  window.dispatchEvent(errorEvent)
}

/**
 * Dispatch an invalid API key error event to show error toast
 * @param errorDetail The invalid API key error details
 */
export function dispatchInvalidApiKeyErrorEvent(
  errorDetail: InvalidApiKeyErrorEventDetail,
): void {
  const errorEvent = new CustomEvent<InvalidApiKeyErrorEventDetail>(
    INVALID_API_KEY_ERROR_EVENT,
    {
      detail: errorDetail,
      bubbles: true,
      cancelable: true,
    },
  )

  window.dispatchEvent(errorEvent)
}

/**
 * Dispatch a structured API error event (with errors array)
 * @param errorData The structured error response data
 */
export function dispatchStructuredApiErrorEvent(
  errorData: StructuredApiErrorResponse,
): void {
  // Combine all error messages into one message
  const messages = errorData.errors.map(e => e.message).join('. ')
  const title = i18n.global.t('api_error_title')

  const errorEvent = new CustomEvent<StructuredApiErrorEventDetail>(
    STRUCTURED_API_ERROR_EVENT,
    {
      detail: {
        title,
        message: messages,
        statusCode: errorData.status_code,
        errors: errorData.errors,
      },
      bubbles: true,
      cancelable: true,
    },
  )

  window.dispatchEvent(errorEvent)
}

/**
 * Handle API errors and dispatch events for visible errors
 * @param error The error object from the API call
 * @returns The original error for further processing
 */
export function handleApiError(error: unknown) {
  try {
    // If the error is an HttpError with errorData already parsed
    if (
      error
      && typeof error === 'object'
      && 'errorData' in error
      && error.errorData
    ) {
      const errorData = error.errorData as
        | ApiErrorResponse
        | StructuredApiErrorResponse

      // Check for wb_error with token issues
      if (isWbTokenError(errorData)) {
        const wbError = errorData as unknown as WbErrorResponse
        dispatchWbTokenErrorEvent({
          title: 'API Token Error',
          message:
            'Your Wildberries API token has expired or is invalid. Please update your token.',
          statusCode: wbError.detail?.status_code || 401,
          error: wbError.error || 'wb_error',
        })
        return error
      }

      // Check for general wb_error (API errors from Wildberries)
      if (isWbApiError(errorData)) {
        const wbError = errorData as unknown as WbErrorResponse
        dispatchWbApiErrorEvent(wbError)
        return error
      }

      // Check for invalid API key error
      if (isInvalidApiKeyError(errorData)) {
        const apiKeyError = errorData as unknown as InvalidApiKeyErrorResponse
        dispatchInvalidApiKeyErrorEvent({
          title: 'Invalid API Key',
          message: apiKeyError.message || 'The provided API key is invalid.',
        })
        // Don't dispatch generic API error for specific errors
        return error
      }

      // Check for structured API error (with status_code and errors array)
      if (isStructuredApiError(errorData)) {
        const structuredError
          = errorData as unknown as StructuredApiErrorResponse
        dispatchStructuredApiErrorEvent(structuredError)
        // Don't dispatch generic API error for structured errors
        return error
      }

      // Check if it's the standard ApiErrorResponse format
      const apiError = errorData as ApiErrorResponse
      if (apiError.visibility) {
        dispatchApiErrorEvent(apiError)
      }
      return error
    }

    // If the error is a Response or has a response property
    if (
      error instanceof Response
      || (error && typeof error === 'object' && 'response' in error)
    ) {
      const response
        = 'response' in error ? (error.response as Response) : (error as Response)

      // Try to parse the error body
      return response
        .clone()
        .json()
        .then((errorData: ApiErrorResponse) => {
          // Check for wb_error with token issues
          if (isWbTokenError(errorData)) {
            const wbError = errorData as unknown as WbErrorResponse
            dispatchWbTokenErrorEvent({
              title: 'API Token Error',
              message:
                'Your Wildberries API token has expired or is invalid. Please update your token.',
              statusCode: wbError.detail?.status_code || 401,
              error: wbError.error || 'wb_error',
            })
            return error
          }

          // Check for general wb_error (API errors from Wildberries)
          if (isWbApiError(errorData)) {
            const wbError = errorData as unknown as WbErrorResponse
            dispatchWbApiErrorEvent(wbError)
            return error
          }

          // Check for invalid API key error
          if (isInvalidApiKeyError(errorData)) {
            const apiKeyError
              = errorData as unknown as InvalidApiKeyErrorResponse
            dispatchInvalidApiKeyErrorEvent({
              title: 'Invalid API Key',
              message: apiKeyError.message || 'The provided API key is invalid.',
            })
            // Don't dispatch generic API error for specific errors
            return error
          }

          // Check for structured API error (with status_code and errors array)
          if (isStructuredApiError(errorData)) {
            const structuredError
              = errorData as unknown as StructuredApiErrorResponse
            dispatchStructuredApiErrorEvent(structuredError)
            // Don't dispatch generic API error for structured errors
            return error
          }

          // Check if it's the standard ApiErrorResponse format
          const apiError = errorData as ApiErrorResponse
          if (apiError.visibility) {
            dispatchApiErrorEvent(apiError)
          }
          return error
        })
        .catch(() => {
          console.error('Failed to parse error response', error)
          return error
        })
    }
  }
  catch (e) {
    console.error('Error handling API error', e)
  }

  return error
}

/**
 * Check if the error is a wb_error related to token issues
 * @param errorData The error response data
 * @returns true if it's a wb token error
 */
function isWbTokenError(errorData: ApiErrorResponse | unknown): boolean {
  try {
    const wbError = errorData as WbErrorResponse
    return (
      wbError
      && wbError.error === 'wb_error'
      && wbError.detail
      && (wbError.detail.api === 'Wildberries'
        || wbError.detail.api?.toLowerCase().includes('wildberries'))
      && (wbError.detail.status_code === 401
        || wbError.detail.status_code === 417)
      && (wbError.detail.message?.includes('unauthorized')
        || wbError.detail.message?.includes('token scope not allowed'))
    )
  }
  catch {
    return false
  }
}

/**
 * Check if the error is a general wb_error (not token related)
 * @param errorData The error response data
 * @returns true if it's a general wb API error
 */
function isWbApiError(errorData: ApiErrorResponse | unknown): boolean {
  try {
    const wbError = errorData as WbErrorResponse
    return (
      wbError
      && wbError.error === 'wb_error'
      && wbError.detail
      && (wbError.detail.api === 'Wildberries'
        || wbError.detail.api?.toLowerCase().includes('wildberries'))
    )
  }
  catch {
    return false
  }
}

/**
 * Check if the error is an invalid API key error
 * @param errorData The error response data
 * @returns true if it's an invalid API key error
 */
function isInvalidApiKeyError(errorData: ApiErrorResponse | unknown): boolean {
  try {
    const apiKeyError = errorData as InvalidApiKeyErrorResponse
    return (
      apiKeyError
      && apiKeyError.error === 'invalid_api_key'
      && typeof apiKeyError.message === 'string'
    )
  }
  catch {
    return false
  }
}

/**
 * Check if the error is a structured API error (with status_code and errors array)
 * @param errorData The error response data
 * @returns true if it's a structured API error
 */
function isStructuredApiError(errorData: ApiErrorResponse | unknown): boolean {
  try {
    const structuredError = errorData as StructuredApiErrorResponse
    return (
      structuredError
      && typeof structuredError.status_code === 'number'
      && Array.isArray(structuredError.errors)
      && structuredError.errors.length > 0
      && structuredError.errors.every(
        e => typeof e.error === 'string' && typeof e.message === 'string',
      )
    )
  }
  catch {
    return false
  }
}
